import { useCallback } from "react";
import { useSelector } from "react-redux";

import { AppState } from "@Store/index";
import useTranslationHook from "hooks/useTranslationHook";

const fill = (text: string, values?: Record<string, string>) =>
  values
    ? Object.keys(values).reduce(
        (out, name) => out.replace(new RegExp(`{${name}}`, "g"), values[name]),
        text,
      )
    : text;

/**
 * `copy(key, english, values?)`: the key's translation, but HomeV3's own sentence-case
 * English on the English site. The shared keys this page reuses carry Title Case English
 * ("See All", "Welcome Back"), which the design does not use.
 *
 * Every other language, amberstudent.cn's Chinese (zh-Hans, zh-Hant, zh-Hans-HK)
 * included, reads `t(key)` from Tolgee like every other page. Until a key is served on
 * this page (created in Tolgee and tagged `home` or `generic`) the English stands in,
 * never a blank: `t` returns "" for a namespaced key while translations load, and the key
 * itself for one it does not hold.
 *
 * `{name}` placeholders in either text are filled from `values`.
 */
const useCopy = () => {
  const { t } = useTranslationHook();
  const userLanguage = useSelector(({ appData }: AppState) => appData.userLanguage);
  return useCallback(
    (key: string, english: string, values?: Record<string, string>) => {
      if (userLanguage === "en") return fill(english, values);
      const translated = t(key, values);
      return translated && translated !== key ? translated : fill(english, values);
    },
    [t, userLanguage],
  );
};

export default useCopy;
